import { useEffect, useState } from 'react'
import { Link, NavLink, useParams, useSearchParams } from 'react-router-dom'
import { ExternalLink, Printer, ArrowLeft, ArrowRight } from 'lucide-react'
import { PageHeader, Panel, Button, Banner, Switch, ConfirmDialog, EmptyState, FormRow, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, patch, del } from '../../lib/api'
import { money, dateShort } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { Term } from '../../components/Term'
import { monthName } from './ClientLeads'
import type { Summary } from './ClientOverview'
import { useCheckout, type CheckoutStart } from '../../lib/checkout'
import { TrialPanel, type TrialInfo } from './Trial'
import { PauseBanners, ChargebackBanners, TabAndWeek, WeeklyInvoices, LeadsSwitch, type WeeklyInfo, type WeeklyBill, type OpenChargeback } from './WeeklyBilling'

export type Billing = {
  billing_cycle?: 'monthly' | 'weekly'
  weekly?: WeeklyInfo | null
  chargebacks?: OpenChargeback[]
  trial?: TrialInfo | null
  closed?: boolean
  can_pay_online: boolean
  org: { name: string; timezone: string; monthly_minimum_cents: number }
  card: { brand: string | null; last4: string | null; exp: string | null } | null
  bank: { name: string | null; last4: string | null } | null
  monthly_method: 'card' | 'bank'
  refill_method: 'card' | 'bank'
  autopay: boolean
  monthly_amount_cents: number
  refill: { enabled: boolean; threshold_cents: number | null; amount_cents: number | null; monthly_max_cents: number | null }
  refilled_this_month_cents: number
  bills: { id: string; period: string; status: 'invoiced' | 'paid'; invoiced_cents: number; invoiced_leads: number; invoiced_at: string; paid_at: string | null; receipt_url: string | null }[]
  payments: { id: string; cash_cents: number; credit_cents: number; method: string | null; note: string | null; created_at: string; receipt_url: string | null }[]
  autopay_runs: { kind: 'minimum' | 'monthly' | 'bill' | 'refill'; period: string; amount_cents: number; status: 'pending' | 'succeeded' | 'failed'; error: string | null; created_at: string }[]
  pending: { id: string; purpose: 'lead_credit' | 'statement'; amount_cents: number; status: 'processing' | 'failed'; error: string | null; created_at: string }[]
}

const AMOUNTS = [10000, 25000, 50000, 100000]
export const brandName = (b: string | null) => (b ? b.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Card')

/** Back from a Stripe page: read the result once, then clean the address. */
function useReturn() {
  const [params, setParams] = useSearchParams()
  const [state] = useState(() => ({ card: params.get('card'), paid: params.get('paid'), bank: params.get('bank') }))
  useEffect(() => { if (params.get('card') || params.get('paid') || params.get('bank')) setParams({}, { replace: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return state
}

export default function ClientBilling() {
  const back = useReturn()
  const b = useApi<Billing>('/api/client/billing')
  const s = useApi<Summary>('/api/client/summary')
  if (b.error) return <ErrorBlock error={b.error} />
  if (!b.data || !s.data) return <LoadingBlock />
  const d = b.data
  // A closed account (a cancelled trial): what happened and its past bills, nothing that takes money.
  if (d.closed) {
    return (
      <>
        <PageHeader title="Billing" subtitle={`Your account with ${d.org.name} is closed.`} />
        {d.trial?.cancelled_at && <div style={{ marginBottom: 'var(--s-6)' }}><TrialPanel trial={d.trial} onChanged={() => { b.reload(); s.reload() }} /></div>}
        {d.bills.length > 0 && <WeeklyInvoices bills={d.bills as WeeklyBill[]} canPay={false} />}
      </>
    )
  }
  const weekly = d.billing_cycle === 'weekly' && d.weekly ? d.weekly : null
  const failed = weekly ? undefined : d.autopay_runs.find((r) => r.status === 'failed')
  const due = weekly ? [] : d.bills.filter((x) => x.status === 'invoiced')

  return (
    <>
      <PageHeader title="Billing" subtitle={weekly ? `Your tab, card and weekly invoices. Payments go to ${d.org.name} through Stripe.` : `Your credit, card, automatic payments and bills. Payments go to ${d.org.name} through Stripe.`}
                  actions={<Link className="btn btn-ghost" to="/portal/pricing">Your prices</Link>} />
      <BillingTabs />
      {(d.trial?.running || d.trial?.cancelled_at) && <div style={{ marginBottom: 'var(--s-6)' }}><TrialPanel trial={d.trial} onChanged={() => { b.reload(); s.reload() }} /></div>}
      {due.length > 0 && (
        <div className="due-bill" role="status">
          <div>
            <strong>{due.length === 1 ? `Your ${monthName(due[0].period)} bill is due` : `${due.length} bills are due`}</strong>
            <span className="muted"> {money(due.reduce((t, x) => t + x.invoiced_cents, 0))} for leads that came after your credit ran out{d.autopay ? '. Your monthly payment pays it automatically.' : '.'}</span>
          </div>
          {d.can_pay_online && !d.autopay && <PayBill period={due[0].period} cents={due[0].invoiced_cents} />}
        </div>
      )}
      {back.card === 'saved' && <Note tone="pos" title="Card saved">It shows here as soon as Stripe confirms it, usually within a minute.</Note>}
      {back.bank === 'saved' && <Note tone="pos" title="Bank account saved">It shows here as soon as Stripe confirms it, usually within a minute. Choose it under Pay with for any automatic payment.</Note>}
      {back.paid === '1' && <Note tone="pos" title="Payment received">Card payments show here within a minute. Bank payments take 3 to 5 business days and show below as processing until then.</Note>}
      {(back.card === 'cancelled' || back.bank === 'cancelled' || back.paid === '0') && <Note tone="warn" title="Nothing changed">You left the payment page. Nothing was charged.</Note>}
      {failed && <Note tone="neg" title="A payment did not go through">{failed.kind === 'bill' ? `The ${monthName(failed.period)} bill` : failed.kind === 'refill' ? 'An auto-refill' : `${monthName(failed.period)}'s monthly payment`}: {failed.error}. Update your card or bank account, then add money or pay below.</Note>}
      <ChargebackBanners chargebacks={d.chargebacks || []} canPay={d.can_pay_online} orgName={d.org.name} />
      {weekly && <PauseBanners info={weekly} bills={d.bills as WeeklyBill[]} canPay={d.can_pay_online} chargebacks={d.chargebacks || []} />}
      {d.can_pay_online && !weekly && (!(d.card || d.bank) || !d.autopay) && (
        <Link to="/portal/billing/setup" className="setup-banner">
          <span className="setup-banner-count">{[Boolean(d.card || d.bank), d.autopay].filter(Boolean).length}/2</span>
          <span><strong>{!(d.card || d.bank) ? 'Set up payments' : 'Turn on your monthly payment'}</strong><span className="muted">{!(d.card || d.bank) ? 'Save a card or connect your bank account, choose a monthly payment and, if you like, auto-refill. Three short steps.' : 'Pick an amount and it is added as credit on the 1st, at the discount.'}</span></span>
          <span className="setup-banner-go">Start <Icon of={ArrowRight} size={15} /></span>
        </Link>
      )}
      {!d.can_pay_online && <Note tone="warn" title="Paying online is not set up yet">Contact {d.org.name} to add money or pay a bill. Everything else here still works.</Note>}

      <div className="portal-hero">
        <AddMoney billing={d} summary={s.data} onDone={b.reload} />
        <CardOnFile billing={d} onChanged={() => { b.reload(); s.reload() }} />
      </div>

      {weekly && <TabAndWeek info={weekly} inTrial={Boolean(d.trial?.running)} onChanged={b.reload} />}
      {weekly && <div style={{ marginBottom: 'var(--s-6)' }}><LeadsSwitch info={weekly} onChanged={() => { b.reload(); s.reload() }} /></div>}
      {!weekly && <AutoPayments billing={d} discountBps={s.data.wallet.discount_bps} onChanged={() => { b.reload(); s.reload() }} />}

      <div className="two-col">
        {weekly ? <WeeklyInvoices bills={d.bills as WeeklyBill[]} canPay={d.can_pay_online} /> : <Panel>
          <div className="panel-header"><h2 className="panel-title"><Term tip="bill">Bills</Term></h2><span className="muted">billed on the 1st</span></div>
          {d.bills.length === 0 ? <EmptyState title="No bills yet" body="Leads past your balance are billed on the 1st of the next month, at full price." /> : (
            <div className="bill-list">
              {d.bills.map((x) => (
                <div key={x.id} className="bill-row">
                  <div>
                    <strong>{monthName(x.period)}</strong>
                    <div className="muted" style={{ fontSize: 13 }}>{x.invoiced_leads} lead{x.invoiced_leads === 1 ? '' : 's'} · <Link to={`/portal/invoice/${x.period}`}>View invoice</Link></div>
                  </div>
                  <strong className="num">{money(x.invoiced_cents)}</strong>
                  <div style={{ textAlign: 'right' }}>
                    {x.status === 'paid'
                      ? <span className="row-tight" style={{ justifyContent: 'flex-end', alignItems: 'center' }}><span className="pill is-good">Paid {dateShort(x.paid_at)}</span>{x.receipt_url && <a href={x.receipt_url} target="_blank" rel="noopener noreferrer" title="Stripe receipt"><Icon of={ExternalLink} size={15} /></a>}</span>
                      : d.can_pay_online ? <PayBill period={x.period} cents={x.invoiced_cents} /> : <span className="pill is-warn">Due</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {due.length > 0 && d.autopay && <p className="muted" style={{ marginBottom: 0 }}>Your monthly payment pays open bills automatically.</p>}
        </Panel>}

        <Panel>
          <div className="panel-header"><h2 className="panel-title">Money added</h2><span className="muted">becomes lead credit</span></div>
          {(d.pending || []).length > 0 && (
            <div className="bill-list" style={{ marginBottom: 'var(--s-5)' }}>
              {d.pending.map((p) => (
                <div key={p.id} className="bill-row">
                  <div>
                    <strong className="num">{money(p.amount_cents)}</strong>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {dateShort(p.created_at)} · bank payment{p.purpose === 'statement' ? ' for a bill' : ''}
                      {p.status === 'processing' ? ', usually 3 to 5 business days' : `. ${(p.error || 'It did not go through').replace(/\.?\s*$/, '.')} Nothing was added; try again or pay by card.`}
                    </div>
                  </div>
                  <span className={`pill ${p.status === 'processing' ? 'is-warn' : 'is-neg'}`}>{p.status === 'processing' ? 'Processing' : 'Did not go through'}</span>
                  <div />
                </div>
              ))}
            </div>
          )}
          {d.payments.length === 0 && !(d.pending || []).length ? <EmptyState title="Nothing added yet" body="Money you add up front buys leads at a discount." /> : d.payments.length === 0 ? null : (
            <div className="bill-list">
              {d.payments.map((p) => (
                <div key={p.id} className="bill-row">
                  <div>
                    <strong className="num">{money(p.cash_cents)}</strong>
                    <div className="muted" style={{ fontSize: 13 }}>{dateShort(p.created_at)} · {p.method === 'bank' ? `${p.note || 'Paid online'}, by bank` : p.note || (p.method ? `paid by ${p.method}` : 'added')}</div>
                  </div>
                  <span className="pill is-good">+{money(p.credit_cents)} credit</span>
                  <div style={{ textAlign: 'right' }}>{p.receipt_url ? <a href={p.receipt_url} target="_blank" rel="noopener noreferrer">Receipt</a> : <span className="muted">no receipt</span>}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  )
}

function Note({ tone, title, children }: { tone: 'pos' | 'warn' | 'neg'; title: string; children: React.ReactNode }) {
  return <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone={tone} title={title}>{children}</Banner></div>
}

/** Pick an amount, see the credit it buys, pay on Stripe's page. */
function AddMoney({ billing, summary, onDone }: { billing: Billing; summary: Summary; onDone: () => void }) {
  const toast = useToast()
  const w = summary.wallet
  const bps = w.discount_bps
  const short = Math.max(0, w.monthly_minimum_cents - w.deposited_this_month_cents)
  const [cents, setCents] = useState<number>(short >= 1000 ? short : 25000)
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const credit = (c: number) => (bps > 0 && bps < 10000 ? Math.round(c / (1 - bps / 10000)) : c)
  const chosen = custom ? Math.round(Number(custom.replace(/[$,\s]/g, '')) * 100) || 0 : cents
  const pct = w.monthly_minimum_cents ? Math.min(100, Math.round((w.deposited_this_month_cents / w.monthly_minimum_cents) * 100)) : 100
  const checkout = useCheckout(`Add ${money(chosen)}`)
  const pay = async () => {
    if (chosen < 1000) return toast('The least you can add online is $10', 'neg')
    setBusy(true)
    try { checkout.open(await post<CheckoutStart>('/api/client/deposits', { amount_cents: chosen }), () => setBusy(false)) }
    catch (e) { toast((e as Error).message, 'neg'); setBusy(false); onDone() }
  }
  return (
    <section className="hero-card is-dark" aria-label="Add money">
      <span className="hero-kicker"><Term tip="credit">Lead credit</Term></span>
      <span className="hero-figure">{money(w.credit_cents)}</span>
      <div className="stack-tight" style={{ gap: 'var(--s-2)' }}>
        <div className="meter-row"><span>This month: {money(w.deposited_this_month_cents)} of the {money(w.monthly_minimum_cents)} minimum</span><span>{pct}%</span></div>
        <div className={pct >= 100 ? 'meter is-full' : 'meter'}><span style={{ width: `${pct}%` }} /></div>
      </div>
      {billing.can_pay_online && (
        <>
          <div className="amount-chips" role="group" aria-label="Amount">
            {AMOUNTS.map((a) => (
              <button key={a} type="button" className="amount-chip" aria-pressed={!custom && cents === a} onClick={() => { setCustom(''); setCents(a) }}>
                {money(a).replace('.00', '')}<small>{money(credit(a)).replace('.00', '')} credit</small>
              </button>
            ))}
          </div>
          <label className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="muted">Other amount</span>
            <input className="field" style={{ maxWidth: 140 }} inputMode="decimal" placeholder="$" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Other amount in dollars" />
          </label>
          <div className="hero-actions">
            <Button onClick={pay} disabled={busy}>{busy ? 'Opening the payment form' : `Add ${money(chosen)}`}</Button>
            {bps > 0 && <span className="muted" style={{ alignSelf: 'center' }}>{money(chosen)} buys {money(credit(chosen))} of leads, {bps / 100}% off</span>}
          </div>
          <span className="muted" style={{ fontSize: 13 }}>Pay by card, or straight from your bank account. Bank payments take 3 to 5 business days to clear.</span>
        </>
      )}
      {checkout.element}
    </section>
  )
}

function CardOnFile({ billing, onChanged }: { billing: Billing; onChanged: () => void }) {
  const toast = useToast()
  const [confirming, setConfirming] = useState<null | 'card' | 'bank'>(null)
  const [busy, setBusy] = useState(false)
  const c = billing.card
  const k = billing.bank
  const cardCheckout = useCheckout(c ? 'Use a different card' : 'Add a card')
  const bankCheckout = useCheckout(k ? 'Use a different bank account' : 'Add a bank account')
  const add = async (what: 'card' | 'bank') => {
    setBusy(true)
    try { (what === 'card' ? cardCheckout : bankCheckout).open(await post<CheckoutStart>(`/api/client/${what}`, {}), () => setBusy(false)) } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  const remove = async () => {
    const what = confirming
    if (!what) return
    try { await del(`/api/client/${what}`); toast(what === 'card' ? 'Card removed' : 'Bank account removed', 'pos'); setConfirming(null); onChanged() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const uses = (m: 'card' | 'bank') => [billing.autopay && billing.monthly_method === m && 'your monthly payment', billing.refill.enabled && billing.refill_method === m && 'auto-refill'].filter(Boolean).join(' and ')
  return (
    <section className="hero-card" aria-label="Payment methods">
      <span className="hero-kicker">Payment methods</span>
      <div className="pay-methods">
        <div className="pay-card">
          <span className="card-chip">{c ? brandName(c.brand).slice(0, 4) : 'CARD'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card-digits">{c ? `${brandName(c.brand)} ending ${c.last4}` : 'No card yet'}</div>
            <div className="muted" style={{ fontSize: 13 }}>{c ? `${c.exp ? `Expires ${c.exp}. ` : ''}Instant.` : 'Instant. Saved the first time you pay by card, or add one now.'}</div>
          </div>
          {billing.can_pay_online && <div className="row-tight">
            <Button variant="secondary" onClick={() => add('card')} disabled={busy}>{c ? 'Change' : 'Add'}</Button>
            {c && <Button variant="ghost" onClick={() => setConfirming('card')}>Remove</Button>}
          </div>}
        </div>
        <div className="pay-card">
          <span className="card-chip">BANK</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card-digits">{k ? `${k.name || 'Bank account'} ending ${k.last4}` : 'No bank account yet'}</div>
            <div className="muted" style={{ fontSize: 13 }}>Takes 3 to 5 business days to clear. Connected through your bank login.</div>
          </div>
          {billing.can_pay_online && <div className="row-tight">
            <Button variant="secondary" onClick={() => add('bank')} disabled={busy}>{k ? 'Change' : 'Add'}</Button>
            {k && <Button variant="ghost" onClick={() => setConfirming('bank')}>Remove</Button>}
          </div>}
        </div>
      </div>
      <p className="muted" style={{ margin: 0, marginTop: 'auto', fontSize: 13 }}>Nothing is charged to save either one. Choose which pays each automatic payment below.</p>
      {cardCheckout.element}{bankCheckout.element}
      {confirming && <ConfirmDialog title={confirming === 'card' ? 'Remove this card?' : 'Remove this bank account?'} tone="neg" confirmLabel="Remove"
        body={billing.billing_cycle === 'weekly' && confirming === 'card' && !billing.bank
          ? 'Your weekly invoices and monthly tab are charged to this card. Without a card on file, new leads stop until you add one (Business Terms, Exhibit C).'
          : uses(confirming) ? `It pays ${uses(confirming)}, which will turn off. You can add it again any time.` : 'You can add it again any time.'}
        onConfirm={remove} onCancel={() => setConfirming(null)} />}
    </section>
  )
}

/** Card or bank, for one automatic payment. A method not on file shows, but cannot be picked. */
function PayWith({ billing, value, onChange, label }: { billing: Billing; value: 'card' | 'bank'; onChange: (m: 'card' | 'bank') => void; label: string }) {
  const opts: { m: 'card' | 'bank'; text: string; on: boolean }[] = [
    { m: 'card', text: billing.card ? `Card ${billing.card.last4}` : 'Card', on: Boolean(billing.card) },
    { m: 'bank', text: billing.bank ? `Bank ${billing.bank.last4}` : 'Bank', on: Boolean(billing.bank) },
  ]
  return (
    <div className="pay-with">
      <span className="muted" style={{ fontSize: 13 }}>Pay with</span>
      <div className="segmented" role="tablist" aria-label={label}>
        {opts.map((o) => (
          <button key={o.m} type="button" role="tab" className="segmented-item" aria-selected={value === o.m}
            disabled={!o.on && value !== o.m} title={o.on ? undefined : `Add a ${o.m === 'card' ? 'card' : 'bank account'} above first`}
            onClick={() => value !== o.m && onChange(o.m)}>{o.text}</button>
        ))}
      </div>
    </div>
  )
}

function PayBill({ period, cents }: { period: string; cents: number }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const checkout = useCheckout(`Pay ${money(cents)}`)
  const pay = async () => {
    setBusy(true)
    try { checkout.open(await post<CheckoutStart>(`/api/client/statements/${period}/pay`, {}), () => setBusy(false)) } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return <><Button onClick={pay} disabled={busy}>{busy ? 'Opening' : `Pay ${money(cents)}`}</Button>{checkout.element}</>
}

type Statement = {
  month: string
  invoice: { lines: { id: string; billed_at: string; invoice_cents: number; tier_name: string | null; summary: string | null }[]; total_cents: number; status: 'invoiced' | 'paid' | null; invoiced_cents: number | null; invoiced_at: string | null; paid_at: string | null }
}

/** One month's auto-buy invoice, laid out to read, print or save as a PDF. */
export function ClientInvoice() {
  const { period } = useParams()
  const st = useApi<Statement>(`/api/client/statement?month=${period}`)
  const s = useApi<Summary>('/api/client/summary')
  if (st.error) return <ErrorBlock error={st.error} />
  if (!st.data || !s.data) return <LoadingBlock />
  const inv = st.data.invoice
  const tz = s.data.org.timezone
  const day = (d: string) => new Date(d).toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' })
  return (
    <>
      <div className="row-tight no-print" style={{ marginBottom: 'var(--s-6)', flexWrap: 'wrap' }}>
        <Link className="btn btn-ghost" to="/portal/billing"><Icon of={ArrowLeft} size={15} /> Billing</Link>
        <Button variant="secondary" onClick={() => window.print()}><Icon of={Printer} size={15} /> Print or save as PDF</Button>
      </div>
      <article className="invoice">
        <header className="invoice-head">
          <div>
            <p className="hero-kicker" style={{ margin: 0 }}>Invoice</p>
            <h1 className="invoice-title">{s.data.org.name}</h1>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0 }}><strong>{monthName(st.data.month)}</strong></p>
            {inv.invoiced_at && <p className="muted" style={{ margin: 0 }}>Issued {dateShort(inv.invoiced_at)}</p>}
            {inv.status === 'paid' && <p style={{ margin: 'var(--s-3) 0 0' }}><span className="paid-stamp">Paid</span></p>}
          </div>
        </header>
        <section className="invoice-parties">
          <div><p className="hero-kicker" style={{ margin: 0 }}>Billed to</p><p style={{ margin: 'var(--s-2) 0 0' }}><strong>{s.data.client.name}</strong></p></div>
          <div><p className="hero-kicker" style={{ margin: 0 }}>For</p><p style={{ margin: 'var(--s-2) 0 0' }}>Leads that came after your credit ran out, at full price</p></div>
        </section>
        {inv.lines.length === 0 ? <EmptyState title="Nothing billed this month" /> : (
          <table>
            <thead><tr><th>Date</th><th>Tier</th><th>Lead</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {inv.lines.map((l) => (
                <tr key={l.id}><td className="nowrap">{day(l.billed_at)}</td><td>{l.tier_name || ''}</td><td>{l.summary || 'Recommendation'}</td><td className="num">{money(l.invoice_cents)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="invoice-total"><span>Total</span><span>{money(inv.invoiced_cents ?? inv.total_cents)}</span></div>
        <p className="muted" style={{ marginTop: 'var(--s-7)', fontSize: 13 }}>Leads paid from your prepaid balance are not on this invoice; they are on your statement. Payments are made to {s.data.org.name} through Stripe.</p>
      </article>
    </>
  )
}

const toDollars = (c: number | null | undefined) => (c == null ? '' : String(c / 100).replace(/\.0+$/, ''))
const toCents = (v: string) => {
  const t = v.replace(/[$,\s]/g, '')
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : null
}

/** The monthly payment and auto-refill: both buy discounted credit from the card on file. */
function AutoPayments({ billing, discountBps, onChanged }: { billing: Billing; discountBps: number; onChanged: () => void }) {
  const toast = useToast()
  const has = (m: 'card' | 'bank') => Boolean(m === 'bank' ? billing.bank : billing.card)
  const monthlyBank = billing.monthly_method === 'bank'
  const refillBank = billing.refill_method === 'bank'
  const min = billing.org.monthly_minimum_cents
  const [monthly, setMonthly] = useState(toDollars(billing.monthly_amount_cents))
  const [threshold, setThreshold] = useState(toDollars(billing.refill.threshold_cents ?? 5000))
  const [amount, setAmount] = useState(toDollars(billing.refill.amount_cents ?? 25000))
  const [max, setMax] = useState(toDollars(billing.refill.monthly_max_cents ?? 100000))
  const [busy, setBusy] = useState(false)
  const credit = (c: number) => (discountBps > 0 && discountBps < 10000 ? Math.round(c / (1 - discountBps / 10000)) : c)
  const m = toCents(monthly)
  const a = toCents(amount)
  const usedPct = billing.refill.monthly_max_cents ? Math.min(100, Math.round((billing.refilled_this_month_cents / billing.refill.monthly_max_cents) * 100)) : 0

  const setAutopay = async (on: boolean) => {
    try { await patch('/api/client/autopay', { on }); toast(on ? 'Monthly payment is on' : 'Monthly payment is off', 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const setMethod = (field: 'monthly_method' | 'refill_method', m: 'card' | 'bank') =>
    save({ [field]: m }, `${field === 'monthly_method' ? 'Monthly payment' : 'Auto-refill'} now paid by ${m === 'bank' ? 'bank account' : 'card'}`)
  const save = async (patchBody: Record<string, unknown>, done: string) => {
    setBusy(true)
    try { await patch('/api/client/payment-settings', patchBody); toast(done, 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const saveMonthly = () => {
    if (m == null || m < min) return toast(`The monthly payment is at least ${money(min)}`, 'neg')
    save({ monthly_amount_cents: m }, 'Monthly payment saved')
  }
  const saveRefill = (enabled: boolean) => {
    const t = toCents(threshold); const x = toCents(max)
    if (enabled && (t == null || a == null || x == null)) return toast('Fill in all three amounts', 'neg')
    save({ refill_enabled: enabled, refill_threshold_cents: t, refill_amount_cents: a, refill_monthly_max_cents: x }, enabled ? 'Auto-refill is on' : 'Auto-refill is off')
  }

  return (
    <div className="two-col">
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Monthly payment</h2>{billing.autopay ? <span className="pill is-good">On</span> : <span className="pill">Off</span>}</div>
        <p style={{ marginTop: 0 }}>Charged when you turn it on, then on the 1st of every month (never twice in a month), as lead credit at {discountBps / 100}% off. It also pays any bill for leads past your credit.{monthlyBank ? ' By bank, the credit arrives when the payment clears, 3 to 5 business days after the 1st; your leads keep coming meanwhile.' : ''}</p>
        <PayWith billing={billing} value={billing.monthly_method} onChange={(m) => setMethod('monthly_method', m)} label="Monthly payment paid with" />
        <div className="row-tight" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormRow label="Each month, dollars" htmlFor="mp-amt" help={m ? `${money(credit(m))} of lead credit${m < min ? `. The least is ${money(min)}.` : ''}` : `At least ${money(min)}`}>
            <input id="mp-amt" className="field" style={{ maxWidth: 160 }} inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} />
          </FormRow>
          <Button variant="secondary" onClick={saveMonthly} disabled={busy || m === billing.monthly_amount_cents}>Save amount</Button>
        </div>
        <div style={{ marginTop: 'var(--s-4)' }}>
          <Switch checked={billing.autopay} disabled={!has(billing.monthly_method)} onChange={(e) => setAutopay(e.target.checked)}
            label={has(billing.monthly_method) ? `Charge ${money(billing.monthly_amount_cents)}${monthlyBank ? ' from your bank' : ''} now, then on the 1st of each month` : `Add a ${monthlyBank ? 'bank account' : 'card'} above to turn this on`} />
        </div>
      </Panel>

      <Panel>
        <div className="panel-header"><h2 className="panel-title"><Term tip="refill">Auto-refill</Term></h2>{billing.refill.enabled ? <span className="pill is-good">On</span> : <span className="pill">Off</span>}</div>
        <p style={{ marginTop: 0 }}>When your credit gets low, add more, also at {discountBps / 100}% off, so leads never pause.{refillBank ? ' By bank, a refill arrives 3 to 5 business days later; leads past your credit are billed meanwhile, so card is quicker here.' : ''}</p>
        <PayWith billing={billing} value={billing.refill_method} onChange={(m) => setMethod('refill_method', m)} label="Auto-refill paid with" />
        <div className="form-grid">
          <FormRow label="When credit falls below" htmlFor="rf-t"><input id="rf-t" className="field" inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} /></FormRow>
          <FormRow label="Add, dollars" htmlFor="rf-a" help={a ? `${money(credit(a))} of credit` : undefined}><input id="rf-a" className="field" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></FormRow>
          <FormRow label="Never more than, a month" htmlFor="rf-m"><input id="rf-m" className="field" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} /></FormRow>
        </div>
        {billing.refill.enabled && billing.refill.monthly_max_cents ? (
          <div className="stack-tight" style={{ gap: 'var(--s-2)', margin: 'var(--s-4) 0' }}>
            <div className="meter-row"><span>Refilled this month: {money(billing.refilled_this_month_cents)} of {money(billing.refill.monthly_max_cents)}</span><span>{usedPct}%</span></div>
            <div className="meter"><span style={{ width: `${usedPct}%` }} /></div>
          </div>
        ) : null}
        <div className="row-tight" style={{ flexWrap: 'wrap', marginTop: 'var(--s-4)' }}>
          {billing.refill.enabled
            ? <><Button variant="secondary" onClick={() => saveRefill(true)} disabled={busy}>Save changes</Button><Button variant="ghost" onClick={() => saveRefill(false)} disabled={busy}>Turn off</Button></>
            : <Button onClick={() => saveRefill(true)} disabled={busy || !has(billing.refill_method)}>{has(billing.refill_method) ? 'Turn on auto-refill' : `Add a ${refillBank ? 'bank account' : 'card'} above to turn this on`}</Button>}
        </div>
      </Panel>
    </div>
  )
}

/** Billing and the statement are one place for money, in two tabs. */
export function BillingTabs() {
  return (
    <nav className="page-tabs" aria-label="Billing">
      <NavLink to="/portal/billing" end className={({ isActive }) => (isActive ? 'page-tab is-on' : 'page-tab')}>Payments and bills</NavLink>
      <NavLink to="/portal/statement" className={({ isActive }) => (isActive ? 'page-tab is-on' : 'page-tab')}>Statement</NavLink>
    </nav>
  )
}
